import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText, CHeader, CLoader, CButton } from '../../components/common';
import { FiCheckCircle } from 'react-icons/fi';
import { IoStar } from 'react-icons/io5';
import { fetchExpertDetails as fetchExpertDetailsAPI, fetchExpertRatings as fetchExpertRatingsAPI, Expert, Rating } from '../../api';

interface ExpertDetails extends Expert {
  headline?: string;
}

const ExpertProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const colors = useAppSelector((state) => state.theme.theme);
  const user = useAppSelector((state) => state.user.user);
  const [loading, setLoading] = useState(false);
  const [expert, setExpert] = useState<ExpertDetails | null>(null);
  const [ratings, setRatings] = useState<Rating[]>([]);

  useEffect(() => {
    fetchExpertDetails();
    fetchRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchExpertDetails = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await fetchExpertDetailsAPI(parseInt(id));
      setExpert(data);
    } catch (error) {
      console.error('Error fetching expert details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    if (!id) return;
    try {
      const data = await fetchExpertRatingsAPI(parseInt(id));
      setRatings(data);
    } catch (error) {
      console.error('Error fetching ratings:', error);
    }
  };

  const handleBookChat = () => {
    navigate(`/expert/${id}/book?type=chat`);
  };

  const handleBookCall = () => {
    navigate(`/expert/${id}/book?type=call`);
  };

  const formatDate = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleDateString();
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i}>
          {React.createElement(IoStar as any, {
            size: 12,
            color: i < Math.floor(rating) ? '#FFD700' : colors.grayScale400,
          })}
        </span>
      );
    }
    return <div className="flex gap-0.5">{stars}</div>;
  };

  if (loading || !expert) {
    return (
      <div
        className="p-4 min-h-screen pb-24"
        style={{ backgroundColor: colors.backgroundColor }}
      >
        <CLoader />
      </div>
    );
  }

  return (
    <div
      className="p-4 min-h-screen pb-24"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <CHeader title="Expert Advice" />

      {/* Expert Card */}
      <div
        className="rounded-xl p-4 mb-4"
        style={{ backgroundColor: colors.inputBackground }}
      >
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="flex flex-col items-center">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold"
              style={{ backgroundColor: colors.primary, color: colors.white }}
            >
              {expert.name.charAt(0)}
            </div>
            {expert.user_avg_rating > 0 ? (
              <div className="flex items-center gap-1 mt-2">
                {React.createElement(IoStar as any, { size: 14, color: '#FFD700' })}
                <CText type="M12" color={colors.textColor}>
                  {expert.user_avg_rating.toFixed(1)}
                </CText>
              </div>
            ) : (
              <CText type="r10" color={colors.grayScale500} className="mt-2">
                No Rating
              </CText>
            )}
          </div>

          {/* Details */}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <CText type="B18" color={colors.textColor}>
                {expert.name}
              </CText>
              {expert.is_verified &&
                React.createElement(FiCheckCircle as any, { size: 18, color: colors.primary })
              }
            </div>
            {expert.company_associated && (
              <CText type="M12" color={colors.white} style={{ marginTop: 2 }}>
                Associated with {expert.company_name}
              </CText>
            )}
            <CText type="r12" color={colors.grayScale400} style={{ marginTop: 4 }}>
              SEBI Registered No. -{' '}
              <span style={{ color: colors.primary }}>{expert.sebi_reg_number}</span>
            </CText>
            <CText type="r12" color={colors.grayScale400}>
              Qualification - {expert.sebi_qualification}
            </CText>
            <CText type="r12" color={colors.grayScale400}>
              Languages: {expert.languages.join(', ')}
            </CText>
            <CText type="r12" color={colors.grayScale400}>
              Exp: {expert.years_of_xp} years
            </CText>
            {expert.headline && (
              <CText type="r14" color={colors.grayScale500} style={{ marginTop: 4 }}>
                {expert.headline}
              </CText>
            )}
          </div>
        </div>

        {/* Rates */}
        <div
          className="flex justify-between mt-4 pt-3 border-t"
          style={{ borderColor: colors.borderColor }}
        >
          <CText type="M14" color={colors.textColor}>
            ₹{expert.call_session_rate}/30 mins Call
          </CText>
          <CText type="M14" color={colors.textColor}>
            ₹{expert.chat_session_rate}/30 mins Chat
          </CText>
        </div>
      </div>

      {/* Ratings Section */}
      {ratings.length > 0 && (
        <div className="mb-6">
          <CText type="B16" color={colors.textColor} style={{ marginBottom: 12 }}>
            Reviews ({ratings.length})
          </CText>
          {ratings.map((rating) => (
            <div
              key={rating.id}
              className="rounded-xl p-4 mb-3"
              style={{ backgroundColor: colors.inputBackground }}
            >
              <div className="flex justify-between items-start mb-2">
                <CText type="M14" color={colors.textColor}>
                  {rating.user_name}
                </CText>
                <CText type="r12" color={colors.grayScale500}>
                  {formatDate(rating.created_at)}
                </CText>
              </div>
              {renderStars(rating.rating)}
              {rating.feedback && (
                <CText type="r14" color={colors.grayScale400} style={{ marginTop: 8 }}>
                  {rating.feedback}
                </CText>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Book Buttons - Only show if user is not an expert */}
      {!user?.is_expert && (
        <div>
          <CText type="B16" color={colors.textColor} style={{ marginBottom: 12 }}>
            Make a New Booking
          </CText>
          <div className="flex gap-3">
            <div className="flex-1">
              <CButton title="Book Chat" onPress={handleBookChat} />
            </div>
            <div className="flex-1">
              <CButton title="Book Call" onPress={handleBookCall} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpertProfile;
